'use client';

import { useEffect, useMemo, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { notifyError } from '@/lib/notify-error';
import { track, type FilterMethod } from '@/lib/telemetry';
import { useSWRConfig } from 'swr';
import {
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { ScrollRail } from '@/components/ui/scroll-rail';
import { CategoryIcon } from '@/components/CategoryIcon';
import { CategoryRulesList } from '@/components/categories/CategoryRulesList';
import { InlineLimitEditor } from '@/components/categories/InlineLimitEditor';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { STATUS_META, applyLimitToOverview, isSingleMonthView } from '@/components/orcamento/helpers';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { useCategoryOverview, type CategoryOverviewScope } from '@/hooks/queries/useCategoryOverview';
import { TRANSACTIONS_MAX_PAGE, useTransactionsPage } from '@/hooks/queries/useTransactionsPage';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import { ApiError, categoryBudgetApi } from '@/lib/api';
import { computeBudgetMonthsForPeriod, type PeriodSelection } from '@/lib/period-presets';
import { groupByDay, withDayTotals } from '@/lib/transactions/utils';
import { useChartSeries } from '@/lib/theme/useThemeColors';
import { cn } from '@/lib/utils';
import { adaptTransactionForScreen } from '../screen/TransactionsScreen';
import { categoryChipStyleFromMeta, getCategoryMeta, type CategoryIndex } from '../screen/categoryIndex';
import { DayHeader } from '../screen/DayHeader';
import { TransactionRow } from '../screen/TransactionRow';
import { TransactionRowSkeleton } from '../screen/TransactionRowSkeleton';
import type { TransactionCategoryFacet } from '@/lib/api';
import type { CategoryOverviewRow } from '@/types';
import type { ScreenTransaction } from '../screen/types';
import { buildSubcategoryRowsFromTxs } from './buildCategoryRows';
import { buildMerchantRows } from './buildMerchantRows';
import {
  filterTransactionsBySelectedSubcategory,
  nextSelectedSubcategoryId,
  resolveCategoryDetailFilterTarget,
} from './categoryDetailFiltering';
import { SubcategoryDonut } from './SubcategoryDonut';
import { TopMerchantsStrip } from './TopMerchantsStrip';

/** The hub's views: breakdown, transaction list, monthly limit, merchant rules. */
export type CategoryDetailView = 'resumo' | 'transactions' | 'limit' | 'rules';

/** First page size of the in-drawer list; each "Mostrar mais" adds `VISIBLE_TX_STEP`. */
const INITIAL_VISIBLE_TX = 50;
const VISIBLE_TX_STEP = 100;

interface ContentProps {
  /** Whether this content is currently visible — gates data fetching + resets. */
  active: boolean;
  categoryId: string | null;
  /** Which view opens first — "Ver lançamentos" entries land straight on the list. */
  initialView?: CategoryDetailView;
  /** Pre-selects a subcategory slice on open (e.g. a child line's "Ver lançamentos"). */
  initialSubcategoryId?: string | null;
  categoryIndex: CategoryIndex;
  /** Period being viewed; used to fetch the full set of transactions for this category. */
  dateRange: { from: Date; to: Date };
  /**
   * Callers on date-insensitive routes (the Categorias directory) pin the hub to a
   * fixed period instead of the global picker — /budget hides that picker, so period
   * labels and limit-editing must not depend on a selection the user can't change
   * there. Omitted, the hub follows the global `usePeriodSelection` as before.
   */
  periodOverride?: PeriodSelection;
  /**
   * Hosts that already hold the overview pass their exact scope so the hub reads the
   * SAME SWR key — no duplicate request, and the host's inline limit edits are never
   * stale here. Omitted, the hub builds a scope from `dateRange` + the period.
   */
  overviewScope?: CategoryOverviewScope;
  /** Current parent-category total from the same facet row that opened this view. */
  categoryAmount?: number;
  /** Current parent-category share from the same facet row that opened this view. */
  categorySharePct?: number;
  /** Fallback share denominator for standalone usages. */
  periodTotal?: number;
  onApplyParentCategoryFilter: (categoryId: string) => void;
  onApplyChildCategoryFilter: (categoryId: string) => void;
  /**
   * Opens the host's transaction detail drawer. Hands over the full transaction —
   * this view fetches its own category page, so the host's paginated main list may
   * not have the tapped id loaded and an id alone could resolve to nothing.
   */
  onOpenTransactionDetails?: (tx: ScreenTransaction) => void;
  /** Opens the host's edit sheet for a custom category (header pencil). */
  onEditCategory?: (row: CategoryOverviewRow) => void;
  /** When provided, renders a back arrow in the header (e.g. to return to a list view). */
  onBack?: () => void;
}

/**
 * The breakdown body for a single category (subcategory donut + top merchants),
 * without its own drawer shell. Rendered either inside `CategoryDetailDrawer`
 * or in-place inside the unified category drawer's detail view.
 */
export function CategoryDetailContent({
  active,
  categoryId,
  initialView = 'resumo',
  initialSubcategoryId = null,
  categoryIndex,
  dateRange,
  periodOverride,
  overviewScope,
  categoryAmount,
  categorySharePct,
  periodTotal,
  onApplyParentCategoryFilter,
  onApplyChildCategoryFilter,
  onOpenTransactionDetails,
  onEditCategory,
  onBack,
}: ContentProps) {
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string | null>(null);
  const [view, setView] = useState<CategoryDetailView>(initialView);
  const [expandedTxId, setExpandedTxId] = useState<string | null>(null);
  const [visibleTxCount, setVisibleTxCount] = useState(INITIAL_VISIBLE_TX);
  const [editingLimit, setEditingLimit] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);

  useEffect(() => {
    setSelectedSubcategoryId(initialSubcategoryId);
  }, [active, categoryId, initialSubcategoryId]);

  useEffect(() => {
    setView(initialView);
    if (active && categoryId) {
      track('ui.tab_viewed', { surface: 'category_detail', tab: initialView, trigger: 'open' });
    }
  }, [active, categoryId, initialView]);

  // Subcategory filter — every entry point (donut, rows, chips) goes through
  // this tracked setter so none of them is invisible to the filter insight.
  const selectSubcategory = (next: string | null, method: FilterMethod) => {
    if (next !== selectedSubcategoryId) {
      track('ui.filter_applied', {
        surface: 'category_detail',
        filter: 'subcategory',
        action: next == null ? 'clear' : 'set',
        method,
      });
    }
    setSelectedSubcategoryId(next);
  };

  // Tab switches inside the hub — the signal for "is the 4-tab drawer worth it?".
  const switchView = (next: CategoryDetailView) => {
    if (next !== view) {
      track('ui.tab_viewed', { surface: 'category_detail', tab: next, trigger: 'switch' });
    }
    setView(next);
  };

  useEffect(() => {
    setExpandedTxId(null);
    setVisibleTxCount(INITIAL_VISIBLE_TX);
    setEditingLimit(false);
  }, [active, categoryId, selectedSubcategoryId, view]);

  // ── Limite / Regras data — the hub is self-sufficient: overview row for the
  // limit metadata, mutations invalidating every overview consumer. On the
  // Categorias page the SWR key matches the page's own fetch, so this is free.
  const formatCurrency = useFormatCurrency();
  const chartSeries = useChartSeries();
  const { value: globalPeriod } = usePeriodSelection();
  const period = periodOverride ?? globalPeriod;
  const singleMonth = isSingleMonthView(period);
  const { data: overview, error: overviewError, mutate: mutateOverview } = useCategoryOverview(
    active && categoryId
      ? overviewScope ?? { from: dateRange.from, to: dateRange.to, budgetMonths: computeBudgetMonthsForPeriod(period) }
      : null,
  );
  const overviewRow = useMemo(
    () => (categoryId ? overview?.rows.find((r) => r.categoryRef === categoryId) ?? null : null),
    [overview, categoryId],
  );
  const authedFetch = useAuthedFetcher();
  const { mutate: mutateGlobal } = useSWRConfig();

  const saveLimit = async (limitAmount: number | null) => {
    if (!categoryId || !overview) return;
    setSavingLimit(true);
    try {
      // Optimistic patch of this drawer's own key — on the Categorias page it is the
      // page's key too, so the rows behind the drawer update instantly as well.
      await mutateOverview(
        async (current) => {
          await authedFetch((token) => categoryBudgetApi.upsertLimit(token, categoryId, { limitAmount }));
          return applyLimitToOverview(current ?? overview, categoryId, limitAmount);
        },
        {
          optimisticData: (current) => applyLimitToOverview(current ?? overview, categoryId, limitAmount),
          rollbackOnError: true,
          populateCache: true,
          // The global mutate below already refetches this key along with every other scope.
          revalidate: false,
        },
      );
      track('action.budget.set_limit', { surface: 'category_detail', cleared: limitAmount == null });
      toast.success(limitAmount == null ? 'Limite removido.' : 'Limite salvo.');
      setEditingLimit(false);
      // Every overview consumer: the Categorias rows, the tx drawer's kebab metadata, this hub.
      void mutateGlobal((key) => Array.isArray(key) && key[0] === 'category-overview');
    } catch (err) {
      if (err instanceof ApiError && err.isLimitExceeded()) return; // global upgrade dialog already shown
      notifyError('category_detail', err, 'Não foi possível salvar o limite.');
    } finally {
      setSavingLimit(false);
    }
  };

  const meta = useMemo(
    () => (categoryId ? getCategoryMeta(categoryIndex, categoryId) : null),
    [categoryId, categoryIndex],
  );
  const style = meta ? categoryChipStyleFromMeta(meta) : null;

  // Fetch the *full* set of transactions for this parent category in the
  // selected period — the main screen list is paginated, so aggregating from
  // it would under-count merchants whose transactions hadn't been loaded yet.
  const { data: categoryPage, error: categoryError, isLoading: isCategoryLoading } = useTransactionsPage(
    active && categoryId
      ? { from: dateRange.from, to: dateRange.to, parentCategoryId: categoryId, limit: TRANSACTIONS_MAX_PAGE }
      : null,
  );

  const categoryTxs = useMemo<ScreenTransaction[]>(
    () => (categoryPage?.items ?? []).map(adaptTransactionForScreen),
    [categoryPage],
  );

  const subcategoryRows = useMemo(
    () => (categoryId ? buildSubcategoryRowsFromTxs(categoryTxs, categoryId, categoryIndex) : []),
    [categoryTxs, categoryId, categoryIndex],
  );

  const locallyFilteredCategoryTxs = useMemo(
    () => filterTransactionsBySelectedSubcategory(categoryTxs, categoryId, selectedSubcategoryId),
    [categoryTxs, categoryId, selectedSubcategoryId],
  );

  const merchantRows = useMemo(
    () => (categoryId ? buildMerchantRows(locallyFilteredCategoryTxs, categoryId) : []),
    [locallyFilteredCategoryTxs, categoryId],
  );

  // Day sections for the in-drawer list — same grouping as the main screen.
  const daySections = useMemo(
    () => withDayTotals(groupByDay(locallyFilteredCategoryTxs, 'date')),
    [locallyFilteredCategoryTxs],
  );
  // Truncate at whole-day granularity so a day's header total never counts
  // transactions the list isn't showing yet.
  const { visibleSections, shownTxCount } = useMemo(() => {
    const sections: typeof daySections = [];
    let shown = 0;
    for (const s of daySections) {
      if (shown >= visibleTxCount) break;
      sections.push(s);
      shown += s.transactions.length;
    }
    return { visibleSections: sections, shownTxCount: shown };
  }, [daySections, visibleTxCount]);
  const hiddenTxCount = locallyFilteredCategoryTxs.length - shownTxCount;

  const categoryTotal = useMemo(
    () => subcategoryRows.reduce((s, r) => s + r.amount, 0),
    [subcategoryRows],
  );

  const displayTotal = categoryAmount ?? categoryTotal;
  const sharePct = categorySharePct ?? (periodTotal && periodTotal > 0 ? (displayTotal / periodTotal) * 100 : 0);
  const hasSubcategorySelection = selectedSubcategoryId !== null;
  const isInitialLoading = active && Boolean(categoryId) && isCategoryLoading && !categoryPage;
  const hasDetailRows = subcategoryRows.length > 0 || merchantRows.length > 0;

  const applyFilter = () => {
    const target = resolveCategoryDetailFilterTarget(categoryId, selectedSubcategoryId);
    if (target?.kind === 'child') onApplyChildCategoryFilter(target.categoryId);
    if (target?.kind === 'parent') onApplyParentCategoryFilter(target.categoryId);
  };

  // Child surfaces emit ids; resolve them against THIS view's fetched transactions
  // before handing off (see onOpenTransactionDetails).
  const openTransactionDetails = (txId: string) => {
    const tx = categoryTxs.find((t) => t.id === txId);
    if (tx) onOpenTransactionDetails?.(tx);
  };

  return (
    <>
      <DrawerHeader className="px-4 py-3">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              aria-label="Voltar para a lista de categorias"
              className="-ml-1 flex size-8 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
            >
              <ArrowLeftIcon className="size-5" />
            </button>
          )}
          {meta && style && (
            <span
              className="flex size-10 shrink-0 items-center justify-center rounded-xl"
              style={{ backgroundColor: style.backgroundColor }}
            >
              <CategoryIcon
                name={meta.iconName}
                className="size-5"
                style={{ color: style.color }}
              />
            </span>
          )}
          <div className="min-w-0 flex-1 text-left">
            <DrawerTitle className="truncate font-display text-lg font-bold">
              {meta?.label ?? 'Categoria'}
            </DrawerTitle>
            <p className="font-mono text-xs text-text-secondary tabular-nums">
              {formatCurrency(displayTotal)} · {sharePct.toFixed(sharePct < 10 ? 1 : 0)}% do período
            </p>
          </div>
          {overviewRow?.source === 'custom' && onEditCategory && (
            <button
              type="button"
              onClick={() => onEditCategory(overviewRow)}
              aria-label={`Editar categoria ${overviewRow.name}`}
              className="flex size-8 shrink-0 items-center justify-center rounded-lg text-text-secondary hover:bg-surface-sunken hover:text-text-primary"
            >
              <PencilSimpleIcon className="size-4.5" />
            </button>
          )}
        </div>
        <DrawerDescription className="sr-only">
          Detalhes da categoria com subcategorias, comerciantes frequentes e lançamentos do período.
        </DrawerDescription>
      </DrawerHeader>

      {/* View switch — the hub's four views, without leaving the drawer. */}
      <div className="scrollbar-hide flex items-center gap-1.5 overflow-x-auto border-t border-border px-4 py-2">
        <HubTab active={view === 'resumo'} onClick={() => switchView('resumo')}>
          Resumo
        </HubTab>
        <HubTab active={view === 'transactions'} onClick={() => switchView('transactions')}>
          Lançamentos
          {categoryPage != null && (
            <span className="font-mono text-2xs tabular-nums opacity-70">{categoryPage.total}</span>
          )}
        </HubTab>
        <HubTab active={view === 'limit'} onClick={() => switchView('limit')}>
          Limite
        </HubTab>
        <HubTab active={view === 'rules'} onClick={() => switchView('rules')}>
          Regras
        </HubTab>
      </div>

      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto border-t border-border',
          view === 'resumo' && 'space-y-5 p-4',
          view === 'limit' && 'space-y-4 p-4',
        )}
      >
        {view === 'resumo' ? (
          isInitialLoading ? (
          <CategoryDetailLoading />
        ) : categoryError ? (
          <CategoryDetailState title="Não foi possível carregar os detalhes." />
        ) : !hasDetailRows ? (
          <CategoryDetailState title="Sem detalhes para esta categoria no período." />
        ) : (
          <>
        <section>
          <h3 className="mb-3 text-2xs font-medium tracking-[0.15em] text-text-tertiary uppercase">
            Subcategorias
          </h3>
          <SubcategoryDonut
            rows={subcategoryRows}
            total={categoryTotal}
            selectedId={selectedSubcategoryId}
            onSelect={(subId) =>
              selectSubcategory(nextSelectedSubcategoryId(selectedSubcategoryId, subId), 'chart')
            }
          />
          {subcategoryRows.length > 0 && (
            <ul className="mt-3 grid grid-cols-1 gap-1.5" role="list">
              {subcategoryRows.slice(0, 6).map((r, i) => {
                const selected = selectedSubcategoryId === r.id;
                const muted = hasSubcategorySelection && !selected;
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        selectSubcategory(nextSelectedSubcategoryId(selectedSubcategoryId, r.id), 'chip')
                      }
                      className={cn(
                        'flex w-full items-center gap-2 rounded-xl px-2 py-1.5 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
                        selected ? 'bg-primary/5 ring-1 ring-primary/25' : 'hover:bg-surface-sunken',
                        muted && 'opacity-55',
                      )}
                    >
                      <span
                        aria-hidden
                        className="size-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: chartSeries[i % chartSeries.length] }}
                      />
                      <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                        {r.label}
                      </span>
                      <span className="font-mono text-sm text-text-primary tabular-nums">
                        {formatCurrency(r.amount)}
                      </span>
                      <span className="font-mono text-2xs text-text-tertiary tabular-nums">
                        {r.pct.toFixed(r.pct < 10 ? 1 : 0)}%
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <TopMerchantsStrip
            rows={merchantRows}
            categoryIndex={categoryIndex}
            onOpenDetails={openTransactionDetails}
          />
        </section>
          </>
        )
        ) : view === 'limit' ? (
          overviewError != null ? (
            <CategoryDetailState title="Não foi possível carregar o limite da categoria." />
          ) : overview == null ? (
            <div
              role="status"
              aria-label="Carregando limite da categoria"
              className="h-40 animate-pulse rounded-xl bg-surface-sunken"
            />
          ) : overviewRow == null ? (
            <CategoryDetailState title="Sem dados de limite para esta categoria no período." />
          ) : overviewRow.kind === 'income' ? (
            <CategoryDetailState title="Receitas não têm limite mensal." />
          ) : overviewRow.kind === 'neutral' ? (
            <CategoryDetailState title="Movimentações internas não têm limite mensal." />
          ) : (
            <CategoryLimitView
              row={overviewRow}
              singleMonth={singleMonth}
              editing={editingLimit}
              saving={savingLimit}
              onStartEditing={() => setEditingLimit(true)}
              onCancelEditing={() => setEditingLimit(false)}
              onCommit={(value) => void saveLimit(value)}
              formatCurrency={formatCurrency}
            />
          )
        ) : view === 'rules' ? (
          <CategoryRulesList categoryRef={categoryId} categoryName={meta?.label} />
        ) : isInitialLoading ? (
          <div role="status" aria-label="Carregando lançamentos da categoria">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <TransactionRowSkeleton key={i} />
            ))}
          </div>
        ) : categoryError ? (
          <div className="p-4">
            <CategoryDetailState title="Não foi possível carregar os lançamentos." />
          </div>
        ) : (
          <>
            {/* Subcategory chips — same selection state the donut uses, so a slice
                picked on the Resumo view arrives here already applied. Desktop gets
                the rail's arrow controls; touch keeps plain swipe. */}
            {subcategoryRows.length > 1 && (
              <ScrollRail className="border-b border-border" railClassName="gap-1.5 px-4 py-2.5">
                <button
                  type="button"
                  aria-pressed={selectedSubcategoryId === null}
                  onClick={() => selectSubcategory(null, 'chip')}
                  className={cn(
                    'shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                    selectedSubcategoryId === null
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-surface text-text-secondary',
                  )}
                >
                  Todas
                </button>
                {subcategoryRows.map((r) => {
                  const selected = selectedSubcategoryId === r.id;
                  return (
                    <button
                      key={r.id}
                      type="button"
                      aria-pressed={selected}
                      onClick={() =>
                        selectSubcategory(nextSelectedSubcategoryId(selectedSubcategoryId, r.id), 'chip')
                      }
                      className={cn(
                        'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                        selected
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-surface text-text-secondary',
                      )}
                    >
                      <span aria-hidden className="size-1.5 rounded-full" style={{ backgroundColor: r.color }} />
                      {r.label}
                    </button>
                  );
                })}
              </ScrollRail>
            )}

            {locallyFilteredCategoryTxs.length === 0 ? (
              <div className="p-4">
                <CategoryDetailState title="Sem lançamentos neste período." />
              </div>
            ) : (
              <>
                {visibleSections.map((s) => (
                  <div key={s.key}>
                    {s.date && <DayHeader date={s.date} total={s.total} />}
                    <ul role="list">
                      {s.transactions.map((tx) => (
                        <li key={tx.id}>
                          <TransactionRow
                            tx={tx}
                            expanded={expandedTxId === tx.id}
                            onToggle={() =>
                              setExpandedTxId((cur) => (cur === tx.id ? null : tx.id))
                            }
                            onOpenDetails={openTransactionDetails}
                            categoryIndex={categoryIndex}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {hiddenTxCount > 0 && (
                  <div className="p-4">
                    <button
                      type="button"
                      onClick={() => setVisibleTxCount((c) => c + VISIBLE_TX_STEP)}
                      className="flex h-11 w-full items-center justify-center rounded-xl border border-border bg-surface text-sm font-semibold text-text-primary hover:bg-surface-sunken"
                    >
                      Mostrar mais ({hiddenTxCount} {hiddenTxCount === 1 ? 'restante' : 'restantes'})
                    </button>
                  </div>
                )}
                {selectedSubcategoryId === null &&
                  categoryPage != null &&
                  categoryPage.total > categoryTxs.length && (
                    <p className="px-5 py-3 text-center text-xs text-text-tertiary">
                      Mostrando os {categoryTxs.length} lançamentos mais recentes de {categoryPage.total}.
                    </p>
                  )}
              </>
            )}
          </>
        )}
      </div>

      {view === 'resumo' && (
        <div className="border-t border-border px-4 pt-3">
          <button
            type="button"
            onClick={() => switchView('transactions')}
            disabled={isInitialLoading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-60"
          >
            Ver lançamentos
            <ArrowRightIcon className="size-4" />
          </button>
        </div>
      )}
      {view === 'transactions' && (
        <div className="border-t border-border px-4 pt-3">
          {/* The old primary CTA, demoted: leaving the drawer for the filtered
              page is an explicit secondary action now, not the default exit. */}
          <button
            type="button"
            onClick={applyFilter}
            disabled={isInitialLoading}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface text-sm font-semibold text-text-primary hover:bg-surface-sunken disabled:opacity-60"
          >
            Ver na página de transações
            <ArrowRightIcon className="size-4" />
          </button>
        </div>
      )}
    </>
  );
}

function HubTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors',
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-text-secondary hover:bg-surface-sunken hover:text-text-primary',
      )}
    >
      {children}
    </button>
  );
}

/**
 * The Limite view: the row's monthly-limit status plus the shared inline
 * editor. Editing is gated to single-month periods — limits are monthly, so a
 * range view has no unambiguous month to write to (same rule as the Categorias
 * rows).
 */
function CategoryLimitView({
  row,
  singleMonth,
  editing,
  saving,
  onStartEditing,
  onCancelEditing,
  onCommit,
  formatCurrency,
}: {
  row: CategoryOverviewRow;
  singleMonth: boolean;
  editing: boolean;
  saving: boolean;
  onStartEditing: () => void;
  onCancelEditing: () => void;
  onCommit: (value: number | null) => void;
  formatCurrency: (value: number) => string;
}) {
  const status = STATUS_META[row.status];
  const burn = row.burnPercent ?? 0;
  const overshoot = row.limit != null && row.spent > row.limit ? row.spent - row.limit : 0;
  const canEdit = singleMonth && !row.isArchived;

  return (
    <>
      <section className="rounded-xl border border-border-subtle p-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-2xs font-medium tracking-[0.15em] text-text-tertiary uppercase">
              {singleMonth ? 'Gasto no mês' : 'Gasto no período'}
            </p>
            <p className="mt-1 font-mono text-lg font-semibold text-text-primary tabular-nums">
              <MoneyValue text={formatCurrency(row.spent)} />
            </p>
          </div>
          <div>
            <p className="text-2xs font-medium tracking-[0.15em] text-text-tertiary uppercase">
              {singleMonth ? 'Limite mensal' : 'Limite do período'}
            </p>
            <p className="mt-1 font-mono text-lg font-semibold text-text-primary tabular-nums">
              {row.limit != null ? <MoneyValue text={formatCurrency(row.limit)} /> : '—'}
            </p>
          </div>
        </div>
        {row.limit != null && (
          <div className="mt-4 flex items-center gap-2.5">
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken">
              <span
                className={`block h-full min-w-1.5 rounded-full ${status.bar}`}
                style={{ width: `${Math.min(100, burn)}%` }}
              />
            </span>
            <span className={`font-mono text-2xs font-semibold tabular-nums ${status.text}`}>
              {Math.round(burn)}%
            </span>
          </div>
        )}
        {overshoot > 0 && (
          <p className="mt-2 font-mono text-2xs font-medium text-error-text tabular-nums">
            <MoneyValue text={formatCurrency(overshoot)} /> acima do limite
          </p>
        )}
      </section>

      <section>
        {!singleMonth ? (
          <p className="rounded-xl bg-surface-sunken px-3 py-4 text-sm text-text-tertiary">
            Limites são mensais — selecione um único mês no período para editar.
          </p>
        ) : row.isArchived ? (
          <p className="rounded-xl bg-surface-sunken px-3 py-4 text-sm text-text-tertiary">
            Categoria arquivada — desarquive para alterar o limite.
          </p>
        ) : editing ? (
          <div>
            <InlineLimitEditor
              // A category without a limit seeds the editor from its 3-month average.
              initialValue={row.limit ?? (row.average3mo > 0 ? row.average3mo : null)}
              saving={saving}
              onCommit={onCommit}
              onCancel={onCancelEditing}
            />
            <p className="mt-1.5 text-2xs text-text-tertiary">
              Limite mensal da categoria
              {row.limit != null ? ' · 0 ou vazio remove o limite' : ''}
            </p>
          </div>
        ) : (
          <button
            type="button"
            disabled={!canEdit || saving}
            onClick={onStartEditing}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface text-sm font-semibold text-text-primary hover:bg-surface-sunken disabled:opacity-60"
          >
            <PencilSimpleIcon className="size-4" />
            {row.limit != null ? 'Alterar limite' : 'Definir limite'}
          </button>
        )}
      </section>

      <p className="text-2xs text-text-tertiary">
        O limite cobre a categoria e todas as suas subcategorias.
        {row.average3mo > 0 && (
          <> Média dos últimos 3 meses: <span className="font-mono tabular-nums">{formatCurrency(row.average3mo)}</span>.</>
        )}
      </p>
    </>
  );
}

function CategoryDetailLoading() {
  return (
    <div role="status" aria-label="Carregando detalhes da categoria" className="space-y-5">
      <section>
        <div className="mb-3 h-3 w-28 animate-pulse rounded bg-surface-sunken" />
        <div className="h-[220px] animate-pulse rounded-xl bg-surface-sunken" />
        <div className="mt-3 grid grid-cols-1 gap-1.5">
          {[0, 1, 2].map((row) => (
            <div key={row} className="h-9 animate-pulse rounded-xl bg-surface-sunken" />
          ))}
        </div>
      </section>
      <section>
        <div className="mb-2 h-3 w-36 animate-pulse rounded bg-surface-sunken" />
        <div className="overflow-hidden rounded-xl border border-border bg-surface">
          {[0, 1, 2].map((row) => (
            <div key={row} className="h-14 animate-pulse border-b border-border bg-surface-sunken/60 last:border-b-0" />
          ))}
        </div>
      </section>
    </div>
  );
}

function CategoryDetailState({ title }: { title: string }) {
  return (
    <div className="flex min-h-64 items-center justify-center rounded-xl bg-surface-sunken px-4 text-center text-sm text-text-tertiary">
      {title}
    </div>
  );
}

interface Props extends Omit<ContentProps, 'active' | 'onBack'> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categoryFacets?: readonly TransactionCategoryFacet[] | undefined;
}

export function CategoryDetailDrawer({ open, onOpenChange, categoryFacets: _categoryFacets, ...content }: Props) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
      <DrawerContent
        className="flex size-full min-h-0 flex-col overflow-hidden px-0 data-[vaul-drawer-direction=bottom]:h-[92dvh] data-[vaul-drawer-direction=bottom]:max-h-[92dvh] data-[vaul-drawer-direction=right]:size-full data-[vaul-drawer-direction=right]:max-h-none sm:max-w-md"
        style={{ paddingBottom: 'calc(1.25rem + env(safe-area-inset-bottom))' }}
      >
        <CategoryDetailContent active={open} {...content} />
      </DrawerContent>
    </Drawer>
  );
}
